import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../App";
import { AppLayout } from "./DashboardPage";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import {
  Users, TrendingDown, Eye, RefreshCw, Repeat,
  ArrowRight, AlertCircle, Save, Loader2, MessageSquare,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STAGE_LABELS = {
  signup: "Sign Up",
  onboarding: "Onboarding",
  goal: "Goal Created",
  income: "Income Added",
  expenses: "Expenses Planned",
  flow: "Flow Viewed",
  reset: "Review & Reset",
  second_cycle: "Second Cycle",
};

const STATUS_STYLES = {
  active: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
  in_progress: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
  stalled: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
};

// ── Section 1: Headline Metrics ──
function HeadlineMetrics({ data }) {
  if (!data) return null;
  const metrics = [
    { label: "Total beta users", value: data.total_beta_users, icon: Users },
    { label: "Completed onboarding", value: `${data.pct_onboarded}%`, icon: ArrowRight },
    { label: "Reached Flow", value: `${data.pct_reached_flow}%`, icon: Eye },
    { label: "First Review & Reset", value: `${data.pct_first_reset}%`, icon: RefreshCw },
    { label: "Second cycle", value: `${data.pct_second_cycle}%`, icon: Repeat },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="headline-metrics">
      {metrics.map((m) => (
        <Card key={m.label} className="border-border/50">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <m.icon className="w-4 h-4 text-muted-foreground" />
              <span className="text-xs text-muted-foreground">{m.label}</span>
            </div>
            <p className="text-2xl font-bold tracking-tight" data-testid={`metric-${m.label.replace(/\s+/g, "-").toLowerCase()}`}>
              {m.value}
            </p>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// ── Section 2: Funnel View ──
function FunnelView({ data, totalUsers }) {
  if (!data?.length) return null;

  const maxCount = totalUsers || data[0]?.count || 1;
  const biggestDropIdx = data.reduce((maxI, item, i, arr) =>
    i > 0 && item.drop_off > arr[maxI].drop_off ? i : maxI, 1);

  return (
    <Card className="border-border/50" data-testid="funnel-view">
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-medium">Progression Funnel</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {data.map((stage, i) => {
          const pct = maxCount > 0 ? (stage.count / maxCount) * 100 : 0;
          const isBiggestDrop = i === biggestDropIdx && stage.drop_off > 0;

          return (
            <div key={stage.key} className="group" data-testid={`funnel-stage-${stage.key}`}>
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">{stage.stage}</span>
                  {isBiggestDrop && (
                    <span className="text-[10px] bg-red-100 text-red-600 dark:bg-red-900/40 dark:text-red-300 px-1.5 py-0.5 rounded" data-testid="biggest-drop-badge">
                      biggest drop
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-3 text-xs text-muted-foreground">
                  <span className="tabular-nums font-medium">{stage.count}</span>
                  {i > 0 && (
                    <>
                      <span className="tabular-nums">{stage.conversion_pct}%</span>
                      {stage.drop_off > 0 && (
                        <span className="text-red-500/70 tabular-nums flex items-center gap-0.5">
                          <TrendingDown className="w-3 h-3" />
                          {stage.drop_off}
                        </span>
                      )}
                    </>
                  )}
                </div>
              </div>
              <div className="h-2 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-700 ease-out ${
                    isBiggestDrop
                      ? "bg-red-400/60 dark:bg-red-500/40"
                      : "bg-[#1a4d3e]/60 dark:bg-[#1a4d3e]/50"
                  }`}
                  style={{ width: `${Math.max(pct, 2)}%` }}
                />
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}

// ── Section 3: Live User Status Table ──
function UserStatusTable({ users }) {
  if (!users?.length) return null;

  return (
    <Card className="border-border/50" data-testid="user-status-table">
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-medium">Live User Status</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border/50">
                <th className="text-left py-2 pr-4 text-xs font-medium text-muted-foreground">User</th>
                <th className="text-left py-2 pr-4 text-xs font-medium text-muted-foreground">Household</th>
                <th className="text-left py-2 pr-4 text-xs font-medium text-muted-foreground">Last Stage</th>
                <th className="text-left py-2 pr-4 text-xs font-medium text-muted-foreground">Inactive</th>
                <th className="text-left py-2 text-xs font-medium text-muted-foreground">Status</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u, i) => (
                <tr key={i} className="border-b border-border/20 last:border-0" data-testid={`user-row-${i}`}>
                  <td className="py-2.5 pr-4">
                    <p className="font-medium text-sm">{u.name || "—"}</p>
                    <p className="text-xs text-muted-foreground">{u.email}</p>
                  </td>
                  <td className="py-2.5 pr-4 text-xs text-muted-foreground capitalize">
                    {u.household_type?.replace(/_/g, " ") || "—"}
                  </td>
                  <td className="py-2.5 pr-4 text-xs">
                    {STAGE_LABELS[u.last_stage] || u.last_stage}
                  </td>
                  <td className="py-2.5 pr-4 text-xs text-muted-foreground tabular-nums">
                    {u.hours_inactive < 1
                      ? "< 1h"
                      : u.hours_inactive < 24
                      ? `${Math.round(u.hours_inactive)}h`
                      : `${Math.round(u.hours_inactive / 24)}d`}
                  </td>
                  <td className="py-2.5">
                    <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium capitalize ${STATUS_STYLES[u.status] || ""}`}>
                      {u.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

// ── Section 4: Drop-off Signal ──
function DropoffSignal({ insight }) {
  if (!insight) return null;

  return (
    <Card className="border-border/50 border-l-4 border-l-amber-400/60" data-testid="dropoff-signal">
      <CardContent className="p-5 flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-amber-500/70 mt-0.5 flex-shrink-0" />
        <p className="text-sm text-foreground/80 leading-relaxed">{insight}</p>
      </CardContent>
    </Card>
  );
}

// ── Section 5: User Moments ──
function UserMoments({ moments }) {
  if (!moments?.length) return null;

  return (
    <Card className="border-border/50" data-testid="user-moments">
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-medium flex items-center gap-2">
          <MessageSquare className="w-4 h-4" />
          User Moments
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {moments.map((m, i) => (
          <div key={i} className="border-l-2 border-[#1a4d3e]/30 pl-3 py-1" data-testid={`moment-${i}`}>
            <p className="text-sm text-foreground/80 italic leading-relaxed">"{m.content}"</p>
            <p className="text-[11px] text-muted-foreground mt-1 capitalize">{m.type}</p>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

// ── Relative time helper ──
function relativeTime(isoString) {
  if (!isoString) return null;
  const date = new Date(isoString);
  if (isNaN(date.getTime())) return null;
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return "yesterday";
  if (days < 7) return `${days}d ago`;
  return date.toLocaleDateString("en-NZ", { day: "numeric", month: "short" });
}

// ── Section 6: Weekly Focus Panel ──
function WeeklyFocusPanel({ initialData, onSave }) {
  const [focus, setFocus] = useState({
    biggest_dropoff: initialData?.biggest_dropoff || "",
    hypothesis: initialData?.hypothesis || "",
    change_to_test: initialData?.change_to_test || "",
  });
  const [saving, setSaving] = useState(false);
  const [editMeta, setEditMeta] = useState({
    by: initialData?.last_edited_by || null,
    at: initialData?.last_edited_at || null,
  });

  const handleSave = async () => {
    setSaving(true);
    const result = await onSave(focus);
    if (result?.last_edited_by) {
      setEditMeta({ by: result.last_edited_by, at: result.last_edited_at });
    }
    setSaving(false);
  };

  const timeAgo = relativeTime(editMeta.at);

  return (
    <Card className="border-border/50" data-testid="weekly-focus-panel">
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-medium">Weekly Focus</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label className="text-xs text-muted-foreground">Biggest drop-off</Label>
          <Input
            value={focus.biggest_dropoff}
            onChange={(e) => setFocus({ ...focus, biggest_dropoff: e.target.value })}
            placeholder="e.g., Onboarding → Goal"
            className="text-sm"
            data-testid="focus-dropoff-input"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-xs text-muted-foreground">Hypothesis why</Label>
          <Textarea
            value={focus.hypothesis}
            onChange={(e) => setFocus({ ...focus, hypothesis: e.target.value })}
            placeholder="e.g., Users don't understand what a goal means in this context"
            className="text-sm min-h-[60px]"
            data-testid="focus-hypothesis-input"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-xs text-muted-foreground">Change to test</Label>
          <Textarea
            value={focus.change_to_test}
            onChange={(e) => setFocus({ ...focus, change_to_test: e.target.value })}
            placeholder="e.g., Add an example goal during onboarding"
            className="text-sm min-h-[60px]"
            data-testid="focus-change-input"
          />
        </div>
        <div className="flex items-center justify-between">
          <Button
            size="sm"
            className="bg-[#1a4d3e] hover:bg-[#1a4d3e]/90"
            onClick={handleSave}
            disabled={saving}
            data-testid="save-focus-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1.5" /> Save Focus</>}
          </Button>
          {editMeta.by && timeAgo && (
            <span className="text-[11px] text-muted-foreground" data-testid="focus-last-edited">
              Last updated by {editMeta.by.split("@")[0]} &middot; {timeAgo}
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// ── Main Page ──
export default function BetaInsightsPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchInsights = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/beta-insights`, { credentials: "include" });
      if (res.ok) {
        setData(await res.json());
      } else {
        toast.error("Failed to load insights");
      }
    } catch {
      toast.error("Could not reach the server");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchInsights();
  }, [fetchInsights]);

  const handleSaveFocus = async (focus) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/beta-insights/weekly-focus`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(focus),
      });
      if (res.ok) {
        const result = await res.json();
        toast.success("Weekly focus saved");
        return result;
      } else {
        toast.error("Failed to save focus");
      }
    } catch {
      toast.error("Could not save focus");
    }
    return null;
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 space-y-8" data-testid="beta-insights-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Beta Insights</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Where is the product breaking for real users?
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={fetchInsights} data-testid="refresh-insights">
            <RefreshCw className="w-4 h-4 mr-1.5" /> Refresh
          </Button>
        </div>

        {/* Section 1: Headline Metrics */}
        <HeadlineMetrics data={data?.headline} />

        {/* Section 4: Drop-off Signal (placed high for visibility) */}
        <DropoffSignal insight={data?.insight} />

        {/* Section 2: Funnel */}
        <FunnelView data={data?.funnel} totalUsers={data?.headline?.total_beta_users} />

        {/* Section 3: Live User Status */}
        <UserStatusTable users={data?.users} />

        {/* Section 5 + 6: Side by side on larger screens */}
        <div className="grid md:grid-cols-2 gap-6">
          <UserMoments moments={data?.moments} />
          <WeeklyFocusPanel initialData={data?.weekly_focus} onSave={handleSaveFocus} />
        </div>
      </div>
    </AppLayout>
  );
}
